import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import OpenBadge from "@/components/OpenBadge";

// Instagram brand-gradient icon (uses inline SVG so stroke can be a gradient)
function IgIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%"   stopColor="#FCAF45" />
          <stop offset="25%"  stopColor="#F56040" />
          <stop offset="50%"  stopColor="#E1306C" />
          <stop offset="75%"  stopColor="#833AB4" />
          <stop offset="100%" stopColor="#405DE6" />
        </linearGradient>
      </defs>
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="url(#ig-grad)" strokeWidth="2" />
      <circle cx="12" cy="12" r="4" stroke="url(#ig-grad)" strokeWidth="2" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="url(#ig-grad)" />
    </svg>
  );
}

const links = [
  { to: "/gallery",   label: "Gallery" },
  { to: "/art-centre",label: "Art Centre" },
  { to: "/ceramic",   label: "Ceramics" },
  { to: "/cafe",      label: "Cafe" },
  { to: "/stage",     label: "Stage" },
  { to: "/coworking", label: "Co-work" },
  { to: "/events",    label: "Events" },
  { to: "/artists",   label: "Artists" },
  { to: "/contact",   label: "Visit" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[hsl(var(--background))]/75 border-b border-soft">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 h-[72px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home-logo">
          <img
            src="https://customer-assets-cm19k8pv.emergentagent.net/job_goa-art-space/artifacts/47po7jix_final%20logo%20no%20background%20.png"
            alt="Carpe Diem Goa"
            className="h-11 md:h-12 w-auto object-contain"
          />
          <div className="leading-tight hidden sm:block">
            <div className="overline" style={{fontSize: '0.55rem'}}>Goa · Est. 2012</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace('/', '')}`}
              className={({ isActive }) =>
                `link-hover text-[13px] tracking-wide ${isActive ? "text-terra" : "text-charcoal"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <OpenBadge />
          <a
            href="https://instagram.com/carpediemgoa"
            target="_blank"
            rel="noreferrer"
            className="link-hover inline-flex items-center gap-1.5 text-[12px] tracking-wide"
            data-testid="nav-instagram"
            title="Follow us on Instagram @carpediemgoa"
            aria-label="Follow @carpediemgoa on Instagram"
          >
            <IgIcon size={18} />
            <span className="hidden xl:inline">@carpediemgoa</span>
          </a>
          <Link to="/events" className="btn-primary" data-testid="nav-book-btn">Book</Link>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-soft bg-cream">
          <div className="px-6 py-6 grid grid-cols-2 gap-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="text-sm py-2"
                data-testid={`nav-mobile-${l.to.replace('/', '')}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/events"
              onClick={() => setOpen(false)}
              className="col-span-2 btn-primary justify-center"
              data-testid="nav-mobile-book-btn"
            >
              Book an event
            </Link>
            <a
              href="https://instagram.com/carpediemgoa"
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="col-span-2 btn-ghost justify-center gap-2"
              data-testid="nav-mobile-instagram"
            >
              <IgIcon size={16} /> Follow @carpediemgoa
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
